import type { Product, ProductCategory } from "../../types";

export type NumericFilterValue = { min?: number; max?: number };

export type SortOption = "relevance" | "price_asc" | "price_desc" | "name_asc" | "name_desc" | "newest";
export const SORT_OPTIONS: SortOption[] = [
  "relevance",
  "price_asc",
  "price_desc",
  "name_asc",
  "name_desc",
  "newest",
];
export function isSortOption(value: string | null | undefined): value is SortOption {
  return typeof value === "string" && (SORT_OPTIONS as string[]).includes(value);
}

/**
 * Applies the visitor's chosen sort on top of the existing relevance order
 * (sortProducts below) — "relevance" is that default order unchanged;
 * everything else replaces it outright. "newest" has no real creation-date
 * field on Product to sort by (never fabricated one) — it uses the
 * catalog's actual DB insertion order instead (getAllProducts orders by
 * rowid, and every filter step before this one is a plain .filter(), which
 * preserves that order), reversed so the most recently added product shows
 * first. That's a genuine "recently added to the catalog" signal, not a
 * manufacture/listing date, but it's real data rather than an invented one.
 */
export function applySortOption(products: Product[], option: SortOption): Product[] {
  switch (option) {
    case "price_asc":
      return products
        .slice()
        .sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
    case "price_desc":
      return products
        .slice()
        .sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));
    case "name_asc":
      return products.slice().sort((a, b) => a.name.localeCompare(b.name, "ru"));
    case "name_desc":
      return products.slice().sort((a, b) => b.name.localeCompare(a.name, "ru"));
    case "newest":
      return products.slice().reverse();
    case "relevance":
    default:
      return products;
  }
}

/**
 * Fisher-Yates, on a copy — callers never need their own copy step first.
 * Used for the catalog's default (no search, no filter, "relevance" sort)
 * browse view beyond its first page, so returning visitors don't always
 * see the same tail of the catalog. Callers are responsible for memoizing
 * the result against a stable dependency (e.g. the loaded product list,
 * not a value recomputed every render) — calling this fresh on every
 * render would reshuffle mid-pagination, which reads as items randomly
 * disappearing/duplicating across pages rather than a one-time random
 * order.
 */
export function shuffleArray<T>(items: T[]): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export const MAIN_CATEGORIES: ProductCategory[] = [
  "Bakery",
  "ServingLines",
  "CoffeeBar",
  "Industrial",
  "Mechanical",
  "Tableware",
  "Warewashing",
  "Chemicals",
  "Thermal",
  "Refrigeration",
  "Laundry",
  "Neutral",
];

export function isProductCategory(value: string | null | undefined): value is ProductCategory {
  return !!value && (MAIN_CATEGORIES as string[]).includes(value);
}

export function matchesNumeric(
  product: Product,
  filters: Record<string, NumericFilterValue>,
  excludeKey?: string
): boolean {
  for (const [key, range] of Object.entries(filters)) {
    if (key === excludeKey) continue;
    const value = product.characteristics?.[key];
    if (typeof value !== "number") return false;
    if (range.min !== undefined && value < range.min) return false;
    if (range.max !== undefined && value > range.max) return false;
  }
  return true;
}

export function matchesSelect(
  product: Product,
  filters: Record<string, Set<string>>,
  excludeKey?: string
): boolean {
  for (const [key, options] of Object.entries(filters)) {
    if (key === excludeKey) continue;
    const value = product.characteristics?.[key];
    if (typeof value !== "string" || !options.has(value)) return false;
  }
  return true;
}

// Pinned to the front of every default product listing (homepage preview
// and the full /catalog page alike), in this exact order, ahead of
// personalized recommendations — a fixed editorial pick rather than
// anything data-driven. A product not matching the active filters is
// simply absent from `products` by the time this runs, so this has no
// effect on filtered/search views beyond bumping order within them.
const FEATURED_PRODUCT_IDS = [
  "electrolux-217634",
  "carlo-migliavacca-dc150easy",
  "hisakage-ac-1500",
  "sayl-buf15",
  "tz-uk-1",
  "LT-02-BLK",
  "matfer-bourgeat-421501",
  "sumisura-azioni-12",
  "ilsa-13220200acv",
  "haeussler-habo-15-2",
  "cuppone-michelangelo-435-2",
];
const featuredRank = new Map(FEATURED_PRODUCT_IDS.map((id, i) => [id, i]));

/**
 * Quietly biases toward whatever a customer's own browsing/search history
 * suggests they'd want to see first — no visible "recommended" label or
 * sort control, it just reads as the natural default order. The fixed
 * FEATURED_PRODUCT_IDS pins take priority over that personalization.
 */
export function sortProducts(products: Product[], recommendedIds: Set<string>): Product[] {
  return products.slice().sort((a, b) => {
    const fa = featuredRank.get(a.id) ?? Infinity;
    const fb = featuredRank.get(b.id) ?? Infinity;
    if (fa !== fb) return fa - fb;
    return Number(recommendedIds.has(b.id)) - Number(recommendedIds.has(a.id));
  });
}

export interface CatalogFilterState {
  activeCategory: ProductCategory | "All";
  activeSubcategory: string | null;
  selectedBrands: Set<string>;
  inStockOnly: boolean;
  numericFilters: Record<string, NumericFilterValue>;
  selectFilters: Record<string, Set<string>>;
  sort: SortOption;
}

/**
 * Serializes the full filter picture (category through characteristic
 * facets) into URL search params — shared by the catalog page (so a reload
 * lands back on the same filtered view) and the homepage preview's "Полный
 * каталог" link (so filters picked there carry into the full catalog).
 */
export function filtersToSearchParams(filters: CatalogFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.activeCategory !== "All") params.set("category", filters.activeCategory);
  if (filters.activeSubcategory) params.set("subcategory", filters.activeSubcategory);
  if (filters.selectedBrands.size > 0) params.set("brands", Array.from(filters.selectedBrands).join(","));
  if (filters.inStockOnly) params.set("stock", "1");
  for (const [key, range] of Object.entries(filters.numericFilters)) {
    if (range.min !== undefined) params.set(`min_${key}`, String(range.min));
    if (range.max !== undefined) params.set(`max_${key}`, String(range.max));
  }
  for (const [key, options] of Object.entries(filters.selectFilters)) {
    if (options.size > 0) params.set(`f_${key}`, Array.from(options).join(","));
  }
  if (filters.sort && filters.sort !== "relevance") params.set("sort", filters.sort);
  return params;
}

export interface CatalogFilterInit {
  category: ProductCategory | "All";
  subcategory: string | null;
  brands: string[];
  inStockOnly: boolean;
  numericFilters: Record<string, NumericFilterValue>;
  selectFilters: Record<string, Set<string>>;
  sort: SortOption;
}

export function parseFiltersFromSearchParams(params: URLSearchParams): CatalogFilterInit {
  const categoryParam = params.get("category");
  const numericFilters: Record<string, NumericFilterValue> = {};
  const selectFilters: Record<string, Set<string>> = {};

  for (const [key, value] of params.entries()) {
    if (key.startsWith("min_")) {
      const field = key.slice(4);
      numericFilters[field] = { ...numericFilters[field], min: Number(value) };
    } else if (key.startsWith("max_")) {
      const field = key.slice(4);
      numericFilters[field] = { ...numericFilters[field], max: Number(value) };
    } else if (key.startsWith("f_")) {
      const field = key.slice(2);
      selectFilters[field] = new Set(value.split(",").filter(Boolean));
    }
  }

  return {
    category: isProductCategory(categoryParam) ? categoryParam : "All",
    subcategory: params.get("subcategory"),
    brands: params.get("brands")?.split(",").filter(Boolean) ?? [],
    inStockOnly: params.get("stock") === "1",
    numericFilters,
    selectFilters,
    sort: isSortOption(params.get("sort")) ? (params.get("sort") as SortOption) : "relevance",
  };
}
